import React, { useEffect, useMemo, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { DateSelectArg, EventDropArg, EventInput } from '@fullcalendar/core';
import { Box, CircularProgress } from '@mui/material';
import { startOfDay } from 'date-fns';
import { useGetCalendarEvents } from './hooks/use-get-calendar-events';

export interface EventDayStripProps {
  startAt: Date;
  endAt: Date;
  allDay: boolean;
  draftTitle?: string;
  onSlotSelect: (start: Date, end: Date, allDay: boolean) => void;
}

interface ResizeEventInfo {
  event: EventDropArg['event'];
  oldEvent: EventDropArg['event'];
  revert: () => void;
}

const DRAFT_EVENT_ID = '__event-day-strip-draft__';
const DAY_MS = 24 * 60 * 60 * 1000;
const EXISTING_EVENT_OPACITY = '0.4';

export function EventDayStrip(props: EventDayStripProps) {
  const { startAt, endAt, allDay, draftTitle, onSlotSelect } = props;
  const calendarRef = useRef<FullCalendar>(null);

  const dayStartMs = startOfDay(startAt).getTime();

  const visibleRange = useMemo(
    () => ({ start: new Date(dayStartMs), end: new Date(dayStartMs + DAY_MS) }),
    [dayStartMs]
  );

  const { events: fetchedEvents, isLoading } = useGetCalendarEvents(visibleRange);

  useEffect(() => {
    calendarRef.current?.getApi().gotoDate(new Date(dayStartMs));
  }, [dayStartMs]);

  const draftEvent: EventInput = useMemo(
    () => ({
      id: DRAFT_EVENT_ID,
      title: draftTitle ?? 'Ny begivenhed',
      start: startAt,
      end: endAt,
      allDay,
      editable: true,
      backgroundColor: 'rgba(25, 118, 210, 0.75)',
      borderColor: '#1976d2',
      textColor: '#ffffff',
    }),
    [startAt.getTime(), endAt.getTime(), allDay, draftTitle]
  );

  const allEvents = useMemo(
    () => [...fetchedEvents.filter(e => e.id !== DRAFT_EVENT_ID), draftEvent],
    [fetchedEvents, draftEvent]
  );

  const initialScrollTime = useMemo(() => {
    const anchor = new Date(Date.now() - 15 * 60 * 1000);
    const hh = String(anchor.getHours()).padStart(2, '0');
    const mm = String(anchor.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}:00`;
  }, []);

  function handleSelect(selectInfo: DateSelectArg) {
    onSlotSelect(selectInfo.start, selectInfo.end, selectInfo.allDay);
    calendarRef.current?.getApi().unselect();
  }

  function handleEventDrop(info: EventDropArg) {
    if (info.event.id !== DRAFT_EVENT_ID || !info.event.start || !info.event.end) {
      info.revert();
      return;
    }
    onSlotSelect(info.event.start, info.event.end, info.event.allDay);
  }

  function handleEventResize(info: ResizeEventInfo) {
    if (info.event.id !== DRAFT_EVENT_ID || !info.event.start || !info.event.end) {
      info.revert();
      return;
    }
    onSlotSelect(info.event.start, info.event.end, info.event.allDay);
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <FullCalendar
        ref={calendarRef}
        plugins={[timeGridPlugin, interactionPlugin]}
        initialView="timeGridDay"
        initialDate={new Date(dayStartMs)}
        headerToolbar={false}
        events={allEvents}
        selectable
        selectMirror={false}
        select={handleSelect}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventDidMount={info => {
          if (info.event.id !== DRAFT_EVENT_ID) {
            info.el.style.opacity = EXISTING_EVENT_OPACITY;
          }
        }}
        height={520}
        locale="da-DK"
        firstDay={1}
        nowIndicator
        allDayText="Hele dagen"
        slotMinTime="06:00:00"
        slotMaxTime="22:00:00"
        slotDuration="00:30:00"
        slotEventOverlap={false}
        scrollTime={initialScrollTime}
      />
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.6)',
            zIndex: 2,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
    </Box>
  );
}
